import { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { usePortalMembership, usePortalScope } from "@/contexts/portalScope";
import { withProgramToken, readProgramToken } from "@/utils/programShareLink";

/**
 * Keeps a scoped affiliate out of the portal until they have actually joined
 * their brand.
 *
 * A white-label portal has exactly one brand, so an affiliate who has neither
 * an enrollment nor a pending application has nothing to look at: an empty
 * dashboard, empty payouts, empty resources. They are sent to the brand's
 * public profile instead, which is where the join call to action lives.
 *
 * A pending application counts as membership — they are waiting on the brand,
 * not on themselves, and bouncing them back to a join button they have already
 * pressed would be a loop.
 *
 * Any `?p=<token>` on the way in rides along, so an affiliate who followed a
 * link for a specific program lands on that program rather than the default.
 *
 * Unscoped affiliates pass straight through. Nothing here can affect the
 * marketplace portal: `usePortalMembership` settles to "not scoped" without a
 * request, and this returns children unchanged.
 */
export function ScopedMembershipGate({ children }: { children: ReactNode }) {
  const { scope, isLoading: scopeLoading } = usePortalScope();
  const { membership, isLoading: membershipLoading } = usePortalMembership();
  const location = useLocation();

  // Never redirect on incomplete information — that is how a member gets
  // thrown out of their own portal on a slow network.
  if (scopeLoading || membershipLoading) return <>{children}</>;
  if (!scope.isScoped || membership.hasMembership) return <>{children}</>;

  const username = membership.homeBrandUsername ?? scope.homeBrandUsername;
  if (!username) return <>{children}</>;

  const destination = withProgramToken(
    `/affiliate/brands/${username}`,
    readProgramToken(new URLSearchParams(location.search)) ?? undefined
  );

  // The brand profile is itself under /affiliate/brands, so redirecting to it
  // from there would loop.
  if (location.pathname.startsWith(`/affiliate/brands/${username}`)) {
    return <>{children}</>;
  }

  return <Navigate to={destination} replace />;
}
